// frontend/src/pages/Admin/BroadcastEmail.tsx
// Page admin : envoi d'emails de broadcast aux abonnés publics (Firebase users)

import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PublicUser {
  point_id: string;
  user_id: string;
  email: string;
  name: string;
  provider: string;
  created_at: string;
  sources: ('firebase_auth' | 'firestore')[];
  legacy_plan: 'monthly' | 'yearly' | null;
  has_api_key: boolean;
  plan: string;
  has_subscription: boolean;
  subscription_period_end: string | null;
}

interface BroadcastResult {
  queued: number;
  broadcast_id: string;
  from_email: string;
}

// ── Templates ─────────────────────────────────────────────────────────────────

const EMAIL_TEMPLATES: Record<string, { subject: string; body: string }> = {
  update: {
    subject: '🚀 Nouvelle mise à jour SmartLawyer',
    body: `Nous sommes heureux de vous annoncer une nouvelle mise à jour !

## 🎯 Nouveautés

- Fonctionnalité X
- Amélioration de la performance Y
- Corrections de bugs

Rendez-vous sur [smartlawyer.ai](https://smartlawyer.ai) pour découvrir les nouveautés.

Cordialement,
L'équipe SmartLawyer`,
  },
  news: {
    subject: '📰 Actualités SmartLawyer',
    body: `Voici les dernières actualités de la plateforme !

## 📢 Annonces

[Votre message ici]

---
L'équipe SmartLawyer`,
  },
  mcp: {
    subject: '🔌 Connectez Claude Desktop à SmartLawyer',
    body: `Saviez-vous que vous pouvez connecter **Claude Desktop** et **Mistral Le Chat** directement à SmartLawyer ?

## 🚀 Le connecteur MCP SmartLawyer

Notre connecteur MCP vous permet d'interroger + de 2 millions de décisions de justice directement depuis votre assistant IA favori — sans quota sur les requêtes.

**Installation en 2 minutes** : [mcp.smartlawyer.ai](https://mcp.smartlawyer.ai)

L'équipe SmartLawyer`,
  },
  custom: {
    subject: '',
    body: '',
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (iso: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const providerIcon = (p: string) => {
  if (p === 'google') return '🔵';
  if (p === 'microsoft') return '🟦';
  if (p === 'email') return '✉️';
  return '🔑';
};

// ── Composant principal ───────────────────────────────────────────────────────

export default function BroadcastEmail() {
  const apiClient = useContext(ChainlitContext);
  const { user } = useAuth();

  // ── State : liste des utilisateurs
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState<'all' | 'paid' | 'free'>('all');
  const [filterKey, setFilterKey] = useState<'all' | 'yes' | 'no'>('all');

  // ── State : sélection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // ── State : composition email
  const [template, setTemplate] = useState<string>('custom');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [senders, setSenders] = useState<string[]>([]);
  const [fromEmail, setFromEmail] = useState('');

  // ── State : envoi
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<BroadcastResult | null>(null);
  const [showResults, setShowResults] = useState(false);

  // ── Fetch users ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetch(apiClient!.buildEndpoint('/api/users/public-users?limit=2000'))
      .then((r) => r.json())
      .then((r) => setUsers(r.users ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
    fetch(apiClient!.buildEndpoint('/api/users/broadcast-senders'), { credentials: 'include' })
      .then((r) => r.json())
      .then((r) => {
        const list: string[] = r.senders ?? [];
        setSenders(list);
        setFromEmail((cur) => cur || list[0] || '');
      })
      .catch(console.error);
  }, [user]);

  // ── Filtrage ──────────────────────────────────────────────────────────────

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    const matchText = !q || u.email.includes(q) || u.name.toLowerCase().includes(q);
    const matchPlan =
      filterPlan === 'all' ||
      (filterPlan === 'paid' && u.has_subscription) ||
      (filterPlan === 'free' && !u.has_subscription);
    const matchKey =
      filterKey === 'all' ||
      (filterKey === 'yes' && u.has_api_key) ||
      (filterKey === 'no' && !u.has_api_key);
    return matchText && matchPlan && matchKey;
  });

  // ── Sélection ─────────────────────────────────────────────────────────────

  const toggleOne = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map((u) => u.email)));
  const selectNone = () => setSelected(new Set());

  const allSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.email));

  // ── Template ──────────────────────────────────────────────────────────────

  const applyTemplate = (key: string) => {
    setTemplate(key);
    if (key !== 'custom') {
      setSubject(EMAIL_TEMPLATES[key].subject);
      setBody(EMAIL_TEMPLATES[key].body);
    }
  };

  // ── Envoi ─────────────────────────────────────────────────────────────────

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      alert('Objet et message requis.');
      return;
    }
    if (selected.size === 0) {
      alert('Sélectionnez au moins un destinataire.');
      return;
    }
    if (!window.confirm(`Envoyer à ${selected.size} destinataire(s) ?`)) return;

    setSending(true);
    setResults(null);
    try {
      const res = await fetch(apiClient!.buildEndpoint('/api/users/broadcast-email'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: subject.trim(),
          message: body.trim(),
          user_emails: Array.from(selected),
          from_email: fromEmail || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setResults(data);
      setShowResults(true);
    } catch (e: any) {
      alert(`Erreur : ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  // ── Stats ligne du haut ───────────────────────────────────────────────────

  const totalPro  = users.filter((u) => u.has_subscription).length;
  const totalFree = users.length - totalPro;
  const totalKeys = users.filter((u) => u.has_api_key).length;

  // ── Render ────────────────────────────────────────────────────────────────

  if (!user) return <Loader />;

  return (
    <Page>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* ── En-tête ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">📧 Broadcast email</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Envoyez un email d'information à vos abonnés publics.
            </p>
          </div>
          <div className="flex gap-3 text-sm">
            <span className="inline-flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-full font-medium">
              👥 {users.length} inscrits
            </span>
            <span className="inline-flex items-center gap-1.5 bg-violet-50 text-violet-700 border border-violet-200 px-3 py-1.5 rounded-full font-medium">
              ✓ Pro {totalPro}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-full font-medium">
              Gratuit {totalFree}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-full font-medium">
              🔑 Clé API {totalKeys}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Colonne gauche : liste des utilisateurs ───────────────── */}
          <div className="space-y-4">
            <div className="bg-card border rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-base flex-1">Destinataires</h2>
                <span className="text-xs text-muted-foreground">
                  {selected.size} sélectionné(s) / {filtered.length} affiché(s)
                </span>
              </div>

              {/* Filtres */}
              <div className="flex flex-wrap gap-2">
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher email ou nom…"
                  className="flex-1 min-w-[160px] text-sm border rounded-lg px-3 py-1.5 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <select
                  value={filterPlan}
                  onChange={(e) => setFilterPlan(e.target.value as any)}
                  className="text-sm border rounded-lg px-2 py-1.5 bg-background"
                >
                  <option value="all">Tous les plans</option>
                  <option value="paid">Pro uniquement</option>
                  <option value="free">Gratuit uniquement</option>
                </select>
                <select
                  value={filterKey}
                  onChange={(e) => setFilterKey(e.target.value as any)}
                  className="text-sm border rounded-lg px-2 py-1.5 bg-background"
                >
                  <option value="all">Clé API : tous</option>
                  <option value="yes">Avec clé API</option>
                  <option value="no">Sans clé API</option>
                </select>
              </div>

              {/* Actions rapides */}
              <div className="flex gap-2 text-xs">
                <button
                  onClick={selectAll}
                  className="text-primary hover:underline font-medium"
                >
                  Tout sélectionner ({filtered.length})
                </button>
                <span className="text-muted-foreground">·</span>
                <button
                  onClick={selectNone}
                  className="text-muted-foreground hover:text-foreground hover:underline"
                >
                  Tout désélectionner
                </button>
                <span className="text-muted-foreground">·</span>
                <button
                  onClick={() => {
                    const proEmails = new Set(filtered.filter((u) => u.has_subscription).map((u) => u.email));
                    setSelected(proEmails);
                  }}
                  className="text-violet-600 hover:underline font-medium"
                >
                  Pro seulement
                </button>
              </div>
            </div>

            {/* Table */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader />
              </div>
            ) : (
              <div className="border rounded-xl overflow-hidden">
                <div className="overflow-y-auto max-h-[520px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm border-b z-10">
                      <tr>
                        <th className="w-8 px-3 py-2 text-left">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => allSelected ? selectNone() : selectAll()}
                            className="rounded"
                          />
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Email</th>
                        <th className="px-3 py-2 text-center font-medium text-muted-foreground">Plan</th>
                        <th className="px-3 py-2 text-center font-medium text-muted-foreground">Clé API</th>
                        <th className="px-3 py-2 text-right font-medium text-muted-foreground">Inscrit le</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filtered.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center py-10 text-muted-foreground">
                            Aucun utilisateur trouvé
                          </td>
                        </tr>
                      )}
                      {filtered.map((u) => (
                        <tr
                          key={u.email}
                          onClick={() => toggleOne(u.email)}
                          className={`cursor-pointer transition-colors hover:bg-muted/40 ${
                            selected.has(u.email) ? 'bg-primary/5' : ''
                          }`}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selected.has(u.email)}
                              onChange={() => toggleOne(u.email)}
                              onClick={(e) => e.stopPropagation()}
                              className="rounded"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium truncate max-w-[200px]">
                              {providerIcon(u.provider)} {u.email}
                            </div>
                            {u.name && u.name !== u.email && (
                              <div className="text-xs text-muted-foreground truncate">{u.name}</div>
                            )}
                            {!u.sources?.includes('firebase_auth') && (
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground border rounded px-1">ancienne app</span>
                            )}
                            {u.legacy_plan && (
                              <span
                                className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground border rounded px-1"
                                title="A payé un abonnement sur l'ancienne app — statut actuel inconnu"
                              >
                                ancien abonné {u.legacy_plan === 'yearly' ? 'annuel' : 'mensuel'}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {u.has_subscription ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 border border-violet-200">
                                ✓ Pro
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                                Gratuit
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {u.has_api_key ? (
                              <span className="text-green-600 font-bold">✓</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-muted-foreground whitespace-nowrap">
                            {formatDate(u.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* ── Colonne droite : compositeur ──────────────────────────── */}
          <div className="space-y-4">
            <div className="bg-card border rounded-xl p-5 space-y-4">
              <h2 className="font-semibold text-base">Composer le message</h2>

              {/* Templates */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-2">
                  Template
                </label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries({ update: '🚀 Mise à jour', news: '📰 Actualités', mcp: '🔌 MCP', custom: '✏️ Personnalisé' }).map(
                    ([key, label]) => (
                      <button
                        key={key}
                        onClick={() => applyTemplate(key)}
                        className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
                          template === key
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background hover:bg-muted border-border text-foreground'
                        }`}
                      >
                        {label}
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Expéditeur */}
              {senders.length > 1 && (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">
                    Expéditeur
                  </label>
                  <select
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    className="w-full text-sm border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {senders.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Objet */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">
                  Objet
                </label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Objet de l'email…"
                  className="w-full text-sm border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {/* Corps */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">
                  Message <span className="font-normal normal-case">(Markdown supporté)</span>
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Rédigez votre message ici…&#10;&#10;Markdown supporté : **gras**, _italique_, [lien](url), ## Titre…"
                  rows={14}
                  className="w-full text-sm border rounded-lg px-3 py-2 bg-background font-mono resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {/* Résumé destinataires */}
              <div className="bg-muted/50 border rounded-lg p-3 text-sm space-y-1">
                <div className="font-medium">
                  📨 {selected.size} destinataire{selected.size !== 1 ? 's' : ''} sélectionné{selected.size !== 1 ? 's' : ''}
                </div>
                {selected.size > 0 && (
                  <div className="text-muted-foreground text-xs">
                    {Array.from(selected).slice(0, 3).join(', ')}
                    {selected.size > 3 && ` … et ${selected.size - 3} autres`}
                  </div>
                )}
              </div>

              {/* Bouton envoi */}
              <button
                onClick={handleSend}
                disabled={sending || selected.size === 0 || !subject.trim() || !body.trim()}
                className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm transition-opacity disabled:opacity-40 hover:opacity-90"
              >
                {sending ? '⏳ Mise en file…' : `📧 Envoyer à ${selected.size} destinataire${selected.size !== 1 ? 's' : ''}`}
              </button>
            </div>

            {/* ── Résultats d'envoi ──────────────────────────────────── */}
            {showResults && results && (
              <div className="bg-card border rounded-xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-base">Résultats</h2>
                  <button
                    onClick={() => setShowResults(false)}
                    className="text-muted-foreground text-xs hover:text-foreground"
                  >
                    Fermer
                  </button>
                </div>
                <p className="text-sm">
                  <span className="text-green-600 font-semibold">
                    ✓ {results.queued} email{results.queued !== 1 ? 's' : ''} mis en file
                  </span>
                  {' '}depuis {results.from_email} — envoi progressif par le cron email-sender.
                </p>
                <Link to="/admin/email-queue" className="text-xs text-primary hover:underline">
                  Suivre l'envoi dans la file d'emails →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
}
